import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  TableContainer,
  Paper,
  Grid,
  Dialog,
  TextField,
  DialogContent,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { writeFile, utils } from "xlsx";
import MDBox from "components/MDBox";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents";
import { ledgerStyles } from "components/Styles/LedgerStyles";
import { CustomPaper, autoSelectStyle, globalStyles } from "components/Styles/globalStyles";
import URLs from "constants/urls";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { useMaterialUIController } from "context";
import Cookie from "js-cookie";

// Currency columns that are summed in the pinned bottom totals row
const CURRENCY_FIELDS = [
  "appliedAmount",
  "disbursedAmount",
  "grossPayoutAmount",
  "dsapayoutAmount",
];

// ── Dynamically resolve the DSA ID field from the object ──────────────────────
// Tries common naming conventions in order of priority
const resolveDsaId = (dsaObj) => {
  if (!dsaObj) return undefined;

  // Try common field name variants (case-insensitive fallback at the end)
  const candidates = ["dsa_Id", "dsaId", "dsa_id", "DSA_Id", "DSAID", "id", "ID"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }

  // Fallback: find any key containing "id" (case-insensitive) with a numeric-like value
  const idKey = Object.keys(dsaObj).find(
    (k) => k.toLowerCase().includes("id") && !isNaN(dsaObj[k])
  );
  return idKey ? dsaObj[idKey] : undefined;
};

// ── Dynamically resolve the DSA Name field from the object ────────────────────
const resolveDsaName = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaName", "dsa_Name", "dsa_name", "DSAName", "name", "Name"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null && dsaObj[key] !== "") {
      return dsaObj[key];
    }
  }
  // Fallback: find any key containing "name"
  const nameKey = Object.keys(dsaObj).find((k) => k.toLowerCase().includes("name"));
  return nameKey ? dsaObj[nameKey] : "";
};

// ── Dynamically resolve the DSA Status field from the object ──────────────────
const resolveDsaStatus = (dsaObj) => {
  if (!dsaObj) return "";
  const candidates = ["dsaStatus", "dsa_Status", "dsa_status", "DSAStatus", "status", "Status"];
  for (const key of candidates) {
    if (dsaObj[key] !== undefined && dsaObj[key] !== null) {
      return dsaObj[key];
    }
  }
  return "";
};

export default function PartialDisbursementsReport() {
  const [rowData, setRowData] = useState([]);
  const [pinnedBottomData, setPinnedBottomData] = useState([]);
  const [dsaList, setDsaList] = useState([]);
  const [selectedDsa, setSelectedDsa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const [controller] = useMaterialUIController();
  const { user } = controller;

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const toISO = (d) => d.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(toISO(firstDayOfMonth));
  const [endDate, setEndDate] = useState(toISO(lastDayOfMonth));

  const gridRef = useRef(null);
  const MInput = inputStyle();

  // ── Helpers ────────────────────────────────────────────────────────────────
  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Pinned totals row ──────────────────────────────────────────────────────
  const computeAndPin = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setPinnedBottomData([]);
      return;
    }
    const totals = { customerName: "Total" };
    CURRENCY_FIELDS.forEach((field) => {
      totals[field] = rows.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
    });
    setPinnedBottomData([totals]);
  }, []);

  // Recompute totals when grid filter changes
  const onFilterChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const visible = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) visible.push(node.data);
    });
    computeAndPin(visible);
  }, [computeAndPin]);

  // ── Fetch DSA list ─────────────────────────────────────────────────────────
  const fetchDsaList = async () => {
    setLoading(true);
    try {
      const res = await fetch(URLs.dsaMasterGetUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookie.get("jwt_token")}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch DSA list");
      const data = await res.json();

      const rawList = Array.isArray(data) ? data : data.data || [];

      // Filter only active DSAs using dynamic status field resolver
      const filtered = rawList.filter((item) => resolveDsaStatus(item) === "S");

      setDsaList(filtered);
    } catch (err) {
      setAlert("Failed to fetch DSA list", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDsaList();
  }, []);

  // ── Fetch report ───────────────────────────────────────────────────────────
  const fetchData = async () => {
    if (!startDate || !endDate || !selectedDsa) {
      setAlert("Please select all required fields.", "error");
      return;
    }

    // Dynamically resolve the DSA ID — no hardcoded field name
    const dsaId = resolveDsaId(selectedDsa);

    if (dsaId === undefined || dsaId === null || dsaId === "") {
      setAlert("Selected DSA has no valid ID. Please contact support.", "error");
      console.error("Could not resolve DSA ID from object:", selectedDsa);
      return;
    }

    setLoading(true);
    try {
      const url = `${URLs.partialFullyDisburseReport}?dsaId=${dsaId}&startDate=${startDate}&endDate=${endDate}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookie.get("jwt_token")}`,
        },
      });
      if (!res.ok) throw new Error("API error");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.data || [];
      setRowData(rows);
      computeAndPin(rows);
    } catch (err) {
      setAlert("Failed to fetch data. Please try again.", "error");
      setRowData([]);
      setPinnedBottomData([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Excel export ───────────────────────────────────────────────────────────
  const downloadData = () => {
    if (!rowData.length) {
      setAlert("No data to download.", "error");
      return;
    }

    // ── 1. Get only filtered/visible rows from the grid ──────────────────────
    const visibleRows = [];
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) visibleRows.push(node.data);
      });
    }
    // Fallback to all rows if grid API not ready
    const exportRows = visibleRows.length > 0 ? visibleRows : rowData;

    // ── 2. Map to clean header names ─────────────────────────────────────────
    const headerMap = {
      customerName: "Customer Name",
      applicationNumber: "Application Number",
      appliedAmount: "Applied Amount",
      bankerName: "Banker Name",
      disbursedAmount: "Disbursed Amount",
      grossPayoutAmount: "Gross Payout",
      dsapayoutAmount: "DSA Payout",
      disburseType: "Disburse Type",
      detailedPaymentStatus: "Payment Status",
    };

    const formattedRows = exportRows.map((row) =>
      Object.fromEntries(Object.entries(headerMap).map(([key, label]) => [label, row[key] ?? ""]))
    );

    // ── 3. Compute totals row ─────────────────────────────────────────────────
    const totalsRow = Object.fromEntries(Object.values(headerMap).map((label) => [label, ""]));
    totalsRow["Customer Name"] = "TOTAL";
    CURRENCY_FIELDS.forEach((field) => {
      const label = headerMap[field];
      if (label) {
        totalsRow[label] = exportRows.reduce((sum, r) => sum + (parseFloat(r[field]) || 0), 0);
      }
    });

    // ── 4. Build worksheet ────────────────────────────────────────────────────
    const wb = utils.book_new();
    const ws = utils.json_to_sheet([...formattedRows, totalsRow]);

    // ── 5. Auto-width columns ─────────────────────────────────────────────────
    const allRows = [...formattedRows, totalsRow];
    const headers = Object.values(headerMap);

    ws["!cols"] = headers.map((header) => {
      const maxLen = Math.max(
        header.length,
        ...allRows.map((row) => {
          const val = row[header];
          return val !== undefined && val !== null ? String(val).length : 0;
        })
      );
      return { wch: maxLen + 4 }; // +4 for padding
    });

    // ── 6. Style totals row (bold + orange background) ────────────────────────
    const totalRowIndex = formattedRows.length + 1; // 1-indexed, +1 for header
    headers.forEach((_, colIndex) => {
      const cellAddress = utils.encode_cell({ r: totalRowIndex, c: colIndex });
      if (!ws[cellAddress]) ws[cellAddress] = { v: "", t: "s" };
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "E59400" } },
        alignment: { horizontal: colIndex >= 2 ? "right" : "left" },
      };
    });

    utils.book_append_sheet(wb, ws, "PartialDisbursements");
    writeFile(wb, "Partial_Disbursements_Report.xlsx");
  };

  // ── Pinned row style (orange) ──────────────────────────────────────────────
  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };

  // ── Column definitions ─────────────────────────────────────────────────────
  const columnDefs = [
    {
      headerName: "Customer Name",
      field: "customerName",
      minWidth: 160,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Application Number",
      field: "applicationNumber",
      minWidth: 140,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Applied Amount",
      field: "appliedAmount",
      minWidth: 140,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Banker Name",
      field: "bankerName",
      minWidth: 160,
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Disbursed Amount",
      field: "disbursedAmount",
      minWidth: 150,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Gross Payout",
      field: "grossPayoutAmount",
      minWidth: 130,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Payout",
      field: "dsapayoutAmount",
      minWidth: 120,
      flex: 1,
      filter: true,
      sortable: true,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Disburse Type",
      field: "disburseType",
      minWidth: 130,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Payment Status",
      field: "detailedPaymentStatus",
      minWidth: 140,
      filter: true,
      sortable: true,
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Loading Dialog */}
      <Dialog open={loading} onClose={() => {}}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      <Grid item xs={12} md={12}>
        <TableContainer component={Paper} sx={globalStyles.accountsTable}>
          {/* ── Filter Bar ── */}
          <MDBox sx={ledgerStyles.mdBoxStyle} mt={1} mb={2}>
            <Grid container spacing={2} alignItems="center" wrap="wrap">
              {/* Start Date */}
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  sx={MInput.b}
                  InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  sx={MInput.b}
                  InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                />
              </Grid>

              {/* Select DSA — dynamically uses resolveDsaName for label */}
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  options={dsaList}
                  getOptionLabel={(option) => resolveDsaName(option)}
                  value={selectedDsa}
                  onChange={(_, v) => setSelectedDsa(v || null)}
                  filterOptions={(options, { inputValue }) => {
                    const input = inputValue.toLowerCase();
                    return options
                      .sort((a, b) => {
                        const aName = resolveDsaName(a).toLowerCase();
                        const bName = resolveDsaName(b).toLowerCase();
                        const aS = aName.startsWith(input);
                        const bS = bName.startsWith(input);
                        return aS === bS ? 0 : aS ? -1 : 1;
                      })
                      .filter((o) => resolveDsaName(o).toLowerCase().includes(input));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select DSA"
                      variant="outlined"
                      fullWidth
                      required
                      sx={{ height: 43, ...autoSelectStyle, ...MInput.b }}
                      InputLabelProps={{ sx: { color: MInput.a } }}
                    />
                  )}
                  PaperComponent={CustomPaper}
                />
              </Grid>

              {/* Display Button */}
              <Grid item xs={12} sm={6} md={2}>
                <StyledButton onClick={fetchData} fullWidth>
                  Display
                </StyledButton>
              </Grid>
            </Grid>

            {/* Excel Export Button */}
            <Grid item xs={12} sm={6} md={2} mt={1}>
              <StyledButton onClick={downloadData}>
                <DownloadForOfflineIcon fontSize="medium" />
              </StyledButton>
            </Grid>
          </MDBox>

          {/* ── AG Grid ── */}
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              enableCellTextSelection={true}
              enableRangeSelection={true}
              columnDefs={columnDefs}
              rowData={rowData}
              pagination={true}
              paginationPageSize={12}
              pinnedBottomRowData={pinnedBottomData}
              onFilterChanged={onFilterChanged}
              getRowStyle={getRowStyle}
            />
          </StyledAgGridContainer>
        </TableContainer>
      </Grid>
    </>
  );
}
